import { AppBar, Toolbar, Typography, Box, Chip, Select, MenuItem, FormControl } from '@mui/material';
import { Monitor, WifiOff } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSources } from '../context/SourcesContext';
import UserProfile from './auth/UserProfile';
import { isEmbedded } from '../lib/embedMode';
import { Button } from '../uikit';

const Header: React.FC = () => {
  const { isConnected, projects, selectedProject, selectProject, canManageProject } = useSources();
  const location = useLocation();
  const navigate = useNavigate();
  // An embedded session is bound to one project and has no RS user, so the
  // project switcher and the profile menu have nothing to offer (COM-636).
  const embedded = isEmbedded();
  // COM-647: the button is "get me back to the articles" on every route that
  // is not the articles page itself - not just `/settings`. When `/sources`
  // was added it kept reading "Profile" there and navigated to the
  // questionnaire, so the sources page had no way back to the main page at
  // all except the browser's Back button. Any future route inherits the
  // right behaviour by default rather than needing to be added here.
  const isMainRoute = location.pathname === '/';

  return (
    <>
      <AppBar position="static" data-testid="app-header">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Monitor sx={{ fontSize: 32 }} />
            <Typography variant="h6" component="h1">
              Regulation Monitoring
            </Typography>

            {/* Project Selector */}
            {!embedded && projects.length > 0 && (
              <Box sx={{ ml: 4 }}>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={selectedProject?.id || ''}
                    onChange={(e) => {
                      const projectId = e.target.value;
                      projectId && selectProject(projectId);
                    }}
                    displayEmpty
                    sx={{
                      color: 'white',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.23)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.87)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'white',
                      },
                      '.MuiSelect-icon': {
                        color: 'white',
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select Project
                    </MenuItem>
                    {projects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>{project.name}</Typography>
                          {
                            project.role === 'OWNER' ?
                              <Chip
                                  label={project.role}
                                  size="small"
                                  color={project.role === 'OWNER' ? 'primary' : 'default'}
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: '20px' }}
                              /> :
                              null
                          }
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {
              !isConnected ?
              <Chip
                  icon={<WifiOff />}
                  label={'Disconnected'}
                  color={'error'}
                  variant="outlined"
                  size="small"
              /> : null
            }

            {/* Getting back to the articles is not a management action, so it
              * is NOT gated on `canManageProject()` - a VIEWER (and every
              * embedded session, which is the whole point of the iframe) needs
              * the way back too. Only "Profile", which opens the project
              * questionnaire, stays gated. */}
            {!isMainRoute && (
              <Button variant="secondary" size="small" onClick={() => navigate('/')}>
                Main
              </Button>
            )}

            {isMainRoute && canManageProject() && (
              <Button variant="secondary" size="small" onClick={() => navigate('/settings')}>
                Profile
              </Button>
            )}

            {!embedded && <UserProfile />}
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Header; 